import { useCallback, useEffect, useState } from "react";
import { Camera, Check, CalendarClock, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";

const fmt = (dt) => new Date(dt).toLocaleString("de-DE", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
const when = (sh) => `${fmt(sh.datetime)}${sh.end_time ? ` – ${sh.end_time}` : ""} Uhr`;

function Shoot({ sh, role, reload }) {
  const [edit, setEdit] = useState(false);
  const [dt, setDt] = useState(sh.datetime);
  const [end, setEnd] = useState(sh.end_time || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = sh.proposed_by === role;
  const other = role === "admin" ? "Kunde" : "Agentur";
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setEdit(false); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirmed = sh.status === "confirmed";

  return (
    <div className={`rounded-2xl border-2 p-4 sm:p-5 transition-colors ${confirmed ? "border-emerald-500 bg-emerald-500/[0.06]" : "border-amber-400/70 bg-amber-400/[0.05]"}`} data-testid={`shoot-card-${sh.id}`}>
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 shrink-0 rounded-xl grid place-items-center ${confirmed ? "bg-emerald-500 text-white" : "bg-amber-400 text-slate-950"}`}><Camera className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Nächster Drehtermin</p>
          <p className="font-display text-xl sm:text-2xl font-bold leading-tight mt-1" data-testid="shoot-datetime">{when(sh)}</p>
          {sh.note && <p className="text-sm text-slate-300 mt-1" data-testid="shoot-note">{sh.note}</p>}
          <p className={`text-sm mt-2 font-medium ${confirmed ? "text-emerald-400" : "text-amber-300"}`} data-testid="shoot-status">
            {confirmed ? "✓ Bestätigt" : mine ? `Wartet auf Bestätigung vom ${other}` : "Bitte bestätigen oder anderen Tag wählen"}
          </p>
        </div>
        {role === "admin" && <button onClick={() => run(() => api.delete(`/shoots/${sh.id}`), "Termin gelöscht")} className="text-slate-500 hover:text-rose-400" data-testid={`shoot-delete-${sh.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      {!edit && (
        <div className="grid grid-cols-2 gap-2 mt-4">
          {!confirmed && !mine
            ? <Button onClick={() => run(() => api.post(`/shoots/${sh.id}/confirm`), "Termin bestätigt")} disabled={busy} className="h-11 bg-emerald-500 hover:bg-emerald-400 text-white gap-1.5" data-testid="shoot-confirm-button"><Check className="h-4 w-4" />Bestätigen</Button>
            : <span />}
          <Button variant="outline" onClick={() => setEdit(true)} className="h-11 gap-1.5 col-start-2 min-w-0 px-2 whitespace-normal text-xs sm:text-sm leading-tight" data-testid="shoot-change-button"><CalendarClock className="h-4 w-4" />Anderen Tag wählen</Button>
        </div>
      )}
      {edit && (
        <div className="mt-4 space-y-2" data-testid="shoot-counter-form">
          <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="h-11" data-testid="shoot-counter-datetime" />
          <label className="flex items-center gap-2 text-sm text-slate-400">bis<Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 flex-1" data-testid="shoot-counter-end" /></label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Kurze Nachricht (optional)" className="h-11" data-testid="shoot-counter-note" />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" onClick={() => setEdit(false)} className="h-11" data-testid="shoot-counter-cancel">Abbrechen</Button>
            <Button onClick={() => run(() => api.post(`/shoots/${sh.id}/counter`, { datetime: dt, end_time: end, note }), "Neuer Termin vorgeschlagen")} disabled={busy || !dt} className="h-11 bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="shoot-counter-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vorschlagen"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanShoot({ reload }) {
  const { clientId } = useClient();
  const [open, setOpen] = useState(false);
  const [dt, setDt] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const save = async () => {
    try { await api.post("/shoots", { client_id: clientId, datetime: dt, end_time: end, note }); toast.success("Drehtermin an Kunden geschickt"); setOpen(false); setDt(""); setEnd(""); setNote(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!open) return <Button variant="outline" onClick={() => setOpen(true)} className="w-full gap-2" data-testid="plan-shoot-button"><Camera className="h-4 w-4" />Drehtermin planen</Button>;
  return (
    <div className="rounded-xl border border-border p-4 space-y-2" data-testid="plan-shoot-form">
      <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="plan-shoot-datetime" />
      <label className="flex items-center gap-2 text-sm text-slate-400">bis<Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="flex-1" data-testid="plan-shoot-end" /></label>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ort / Inhalt, z.B. Produktion Nürnberg" data-testid="plan-shoot-note" />
      <div className="grid grid-cols-2 gap-2">
        <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
        <Button onClick={save} disabled={!dt} className="bg-teal-500 hover:bg-teal-400 text-slate-950" data-testid="plan-shoot-submit">Senden</Button>
      </div>
    </div>
  );
}

export default function ShootSection() {
  const { user } = useAuth();
  const { clientId } = useClient();
  const [shoots, setShoots] = useState([]);
  const load = useCallback(() => api.get("/shoots", { params: { client_id: clientId } }).then((r) => setShoots(r.data)), [clientId]);
  useEffect(() => { load(); }, [load]);
  if (user.role !== "admin" && shoots.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="shoot-section">
      {shoots.map((sh) => <Shoot key={sh.id} sh={sh} role={user.role} reload={load} />)}
      {user.role === "admin" && <PlanShoot reload={load} />}
    </div>
  );
}
